// ---------------------------------------------------------------------------
// LiveBellFeed — realtime stream of bell-play events for the selected school.
// ---------------------------------------------------------------------------
// Polls the BellLog API (search, newest-first, filtered by the console's
// selected school) on a short interval and renders an interactive, live status
// tracker: play/pause, status filter chips, animated new rows, relative "time
// ago" and a rolling count of today's plays. Styled with the console's scoped
// `.dgc` classes so it sits seamlessly above the week grid.
// ---------------------------------------------------------------------------
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {searchBellLogs} from "../../../../Services/BellLogService";

const POLL_MS = 10 * 1000;
const FEED_LIMIT = 25;

// Map a raw backend status string onto the console's visual status buckets so
// colours match the week grid (played / late / failed / offline / pending).
const normalizeStatus = (raw) => {
    const v = String(raw || "").toUpperCase();
    if (v.includes("FAIL") || v.includes("ERROR")) return "failed";
    if (v.includes("LATE")) return "late";
    if (v.includes("OFFLINE")) return "offline";
    if (v.includes("PEND") || v.includes("QUEUE")) return "pending";
    return "played";
};

const STATUS_META = {
    played: {icon: "🔔", label: "Played"},
    late: {icon: "⏱", label: "Late"},
    failed: {icon: "⚠", label: "Failed"},
    offline: {icon: "⚡", label: "Offline"},
    pending: {icon: "…", label: "Pending"},
};

const getValue = (log, keys, fallback = null) => {
    const key = keys.find((item) => log?.[item] !== undefined && log?.[item] !== null && log?.[item] !== "");
    return key ? log[key] : fallback;
};

const getEventList = (data) =>
    (Array.isArray(data) ? data : data?.content || data?.bellLogs || data?.logs || data?.items) || [];

const eventId = (log, index) =>
    getValue(log, ["id", "bellLogId"], null) ?? `${getValue(log, ["playedAt", "createdAt", "timestamp"], "")}-${index}`;

const timeAgo = (value) => {
    if (!value) return "";
    const then = new Date(value).getTime();
    if (Number.isNaN(then)) return "";
    const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (secs < 10) return "just now";
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.round(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return new Date(value).toLocaleDateString([], {month: "short", day: "numeric"});
};

const clockTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit", second: "2-digit"});
};

const isToday = (value) => {
    if (!value) return false;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

const FILTERS = ["played", "late", "failed", "offline"];

function LiveBellFeed({schoolId, i18n}) {
    const t = i18n?.t || ((s) => s);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [live, setLive] = useState(true);
    const [error, setError] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [filter, setFilter] = useState(() => new Set());
    // Ids seen on the previous poll, so genuinely-new rows can play the rise-in.
    const seenIds = useRef(new Set());
    const isMounted = useRef(true);
    const [, forceTick] = useState(0);

    const load = useCallback(async () => {
        if (!schoolId) {
            setEvents([]);
            setLoading(false);
            return;
        }
        try {
            const response = await searchBellLogs({schoolId: Number(schoolId)}, 0, FEED_LIMIT);
            if (!isMounted.current) return;
            setEvents(getEventList(response?.data));
            setError(false);
            setUpdatedAt(Date.now());
        } catch (e) {
            if (isMounted.current) setError(true);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, [schoolId]);

    // Reset the feed whenever the console switches schools.
    useEffect(() => {
        seenIds.current = new Set();
        setLoading(true);
        load();
    }, [load]);

    // Poll only while "live" and the tab is visible.
    useEffect(() => {
        if (!live) return undefined;
        const id = setInterval(() => {
            if (!document.hidden) load();
        }, POLL_MS);
        return () => clearInterval(id);
    }, [live, load]);

    // Re-render every 20s so relative "time ago" labels stay fresh even between polls.
    useEffect(() => {
        const id = setInterval(() => forceTick((n) => n + 1), 20 * 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const decorated = useMemo(
        () =>
            events.map((log, index) => {
                const id = eventId(log, index);
                const status = normalizeStatus(getValue(log, ["status"], "PLAYED"));
                const when = getValue(log, ["playedAt", "createdAt", "timestamp"], null);
                return {
                    id,
                    status,
                    when,
                    isNew: !seenIds.current.has(id),
                    name: getValue(log, ["bellName", "name", "bell"], "Bell"),
                    type: getValue(log, ["bellType", "type"], null),
                    hourTime: getValue(log, ["hourTime", "scheduledTime"], null),
                    source: getValue(log, ["source"], null),
                };
            }),
        [events]
    );

    // Mark the current batch as seen after render so next poll's diff is correct.
    useEffect(() => {
        const next = new Set(seenIds.current);
        decorated.forEach((e) => next.add(e.id));
        seenIds.current = next;
    }, [decorated]);

    const visible = useMemo(
        () => (filter.size === 0 ? decorated : decorated.filter((e) => filter.has(e.status))),
        [decorated, filter]
    );

    const counts = useMemo(() => {
        const acc = {played: 0, late: 0, failed: 0, offline: 0, today: 0};
        decorated.forEach((e) => {
            if (acc[e.status] !== undefined) acc[e.status] += 1;
            if (isToday(e.when)) acc.today += 1;
        });
        return acc;
    }, [decorated]);

    const toggleFilter = (f) =>
        setFilter((prev) => {
            const n = new Set(prev);
            n.has(f) ? n.delete(f) : n.add(f);
            return n;
        });

    return (
        <section className="card livefeed">
            <div className="card-head">
                <div className="card-title">
                    {t("liveFeed") === "liveFeed" ? "Live Bell Feed" : t("liveFeed")}
                    <span>{counts.today} today</span>
                </div>

                <div className={`lf-live ${live ? "" : "paused"}`}>
                    <i className={`rec ${live ? "pulse" : ""}`} />
                    {live ? "Live" : "Paused"}
                </div>

                <div className="filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f}
                            className="fchip"
                            aria-pressed={filter.has(f)}
                            onClick={() => toggleFilter(f)}
                            title={`Filter ${STATUS_META[f].label}`}
                        >
                            <i className={`sw sw-${f}`} />
                            {STATUS_META[f].label}
                            <b className="fcount">{counts[f]}</b>
                        </button>
                    ))}
                    <button className="fchip lf-toggle" onClick={() => setLive((v) => !v)}>
                        {live ? "❚❚ Pause" : "▶ Resume"}
                    </button>
                    <button className="fchip lf-toggle" onClick={load} title="Refresh now">
                        ⟳
                    </button>
                </div>
            </div>

            <div className="lf-list">
                {loading && events.length === 0 ? (
                    Array.from({length: 4}).map((_, i) => (
                        <div className="lf-item" key={`skel-${i}`}>
                            <div className="lf-badge skel" />
                            <div className="lf-main">
                                <div className="skel" style={{height: 13, width: "45%", marginBottom: 7}} />
                                <div className="skel" style={{height: 11, width: "30%"}} />
                            </div>
                            <div className="skel" style={{height: 12, width: 54}} />
                        </div>
                    ))
                ) : visible.length === 0 ? (
                    <div className="state">
                        <div className="st">{error ? "Feed unavailable" : "No bell activity yet"}</div>
                        <div>
                            {error
                                ? "Couldn't reach the bell-log service. Retrying…"
                                : "Played bells for this school will stream in here as they ring."}
                        </div>
                    </div>
                ) : (
                    visible.map((e) => (
                        <div className={`lf-item ${e.isNew ? "rise" : ""}`} key={e.id}>
                            <div className={`lf-badge s-${e.status}`}>{STATUS_META[e.status].icon}</div>
                            <div className="lf-main">
                                <div className="lf-name">{e.name}</div>
                                <div className="lf-sub">
                                    {e.hourTime ? <span className="mono">{e.hourTime}</span> : null}
                                    {e.type ? <span className="chip mut">{e.type}</span> : null}
                                    {e.source ? <span>{e.source}</span> : null}
                                </div>
                            </div>
                            <div className="lf-right">
                                <div className={`lf-pill p-${e.status}`}>{STATUS_META[e.status].label}</div>
                                <div className="lf-time mono" title={clockTime(e.when)}>
                                    {timeAgo(e.when)}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="lf-foot">
                <span className="mono">
                    {updatedAt ? `Updated ${new Date(updatedAt).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit", second: "2-digit"})}` : "—"}
                </span>
                <span className="lf-hint">Auto-refreshes every {POLL_MS / 1000}s</span>
            </div>
        </section>
    );
}

export default LiveBellFeed;
